import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { REGISTER_URL } from "@/data/site";

const chapters = [
  { n: "01", title: "Simplify daily operations", body: "We replace scattered spreadsheets, WhatsApp groups and paper registers with a single, calm dashboard. Every batch, student and payment lives in one place — nothing slips through the cracks." },
  { n: "02", title: "Save institutes real time", body: "Attendance in seconds. Fee reminders that fire automatically after every 8 completed classes. Class reminders that reduce no-shows. The admin work that used to take hours now takes minutes." },
  { n: "03", title: "Improve student management", body: "Clear records, confirmed attendance and insightful reports mean better decisions, happier parents and students who stay longer. Great teaching deserves great tools." },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        overline="About"
        titleLines={["Built for the", "people who teach."]}
        subtitle="Activity Hub Manager exists to help activity institutes simplify daily operations, save time, and improve student management."
      />

      {/* Mission statement */}
      <section className="mx-auto max-w-4xl px-5 py-12 lg:px-8">
        <Reveal>
          <p className="font-heading text-2xl font-medium leading-relaxed tracking-tight text-white sm:text-3xl">
            “We believe running an academy should feel effortless. Our mission is to give every
            activity institute the tools to <span className="text-brand">simplify daily operations, save time, and improve student management</span> — so educators can focus on what they do best.”
          </p>
        </Reveal>
      </section>

      {/* Numbered manifesto */}
      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="space-y-4">
          {chapters.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08}>
              <div className="group relative grid grid-cols-1 items-center gap-6 overflow-hidden rounded-2xl border border-zinc-800 bg-surface p-8 transition-colors hover:border-brand/50 md:grid-cols-[220px_1fr] lg:p-12">
                <span className="font-heading text-7xl font-black leading-none tracking-tighter text-transparent [-webkit-text-stroke:1px_rgba(255,255,255,0.12)] transition-all group-hover:[-webkit-text-stroke:1px_rgba(0,85,255,0.6)] lg:text-8xl">
                  {c.n}
                </span>
                <div>
                  <h3 className="font-heading text-2xl font-bold tracking-tight text-white">{c.title}</h3>
                  <p className="mt-3 max-w-2xl text-zinc-400">{c.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Values / stats */}
      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-8">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { k: "Cloud-first", v: "Access your institute securely from any device, anywhere." },
            { k: "Automation-led", v: "Reminders and fee nudges run themselves, so you don't have to." },
            { k: "Institute-obsessed", v: "Every feature is shaped by real academies and their needs." },
          ].map((x, i) => (
            <Reveal key={x.k} delay={i * 0.1}>
              <div className="h-full rounded-xl border border-zinc-800 bg-surface p-8">
                <h4 className="font-heading text-lg font-semibold text-brand">{x.k}</h4>
                <p className="mt-3 text-sm text-zinc-400">{x.v}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-14 text-center">
          <a href={REGISTER_URL} data-testid="about-cta-btn" className="inline-flex items-center gap-2 rounded-md bg-brand px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover">
            Join Activity Hub <ArrowRight className="h-4 w-4" />
          </a>
        </Reveal>
      </section>
    </div>
  );
}
